import { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { canProject, type ProjectPermission } from '@allemnii-pulse/shared';
import { ProjectProvider, useProject } from './ProjectProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useUnreadCount } from '../discussions/data.js';

type Tab = { to: string; label: string; end?: boolean; requires?: ProjectPermission };

const TABS: Tab[] = [
  { to: '', label: 'Overview', end: true },
  { to: 'prd', label: 'PRD' },
  { to: 'tdd', label: 'TDD' },
  { to: 'tasks', label: 'Tasks' },
  { to: 'discussions', label: 'Discussions', requires: 'discussion.participate' },
  { to: 'deliverables', label: 'Deliverables' },
  { to: 'activity', label: 'Activity', requires: 'project.activity.read' },
  { to: 'settings', label: 'Settings' },
];

/** Training projects replace PRD and TDD with sources and sessions — see schemas/training.ts. */
const TRAINING_TABS: Tab[] = [
  { to: '', label: 'Overview', end: true },
  { to: 'sources', label: 'Sources' },
  { to: 'sessions', label: 'Sessions' },
  { to: 'tasks', label: 'Tasks' },
  { to: 'discussions', label: 'Discussions', requires: 'discussion.participate' },
  { to: 'deliverables', label: 'Deliverables' },
  { to: 'activity', label: 'Activity', requires: 'project.activity.read' },
  { to: 'settings', label: 'Settings' },
];

/** The Discussions tab's unread badge — messages from others since you last read it. */
function UnreadBadge() {
  const { projectId, role } = useProject();
  const { user } = useAuth();
  const { count, capped } = useUnreadCount(
    user?.uid,
    projectId,
    canProject(role, 'discussion.participate'),
  );
  if (count === 0) return null;
  return (
    <span
      aria-label={`${count}${capped ? '+' : ''} unread`}
      className="ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-accent-fg"
    >
      {capped ? `${count}+` : count}
    </span>
  );
}

function ProjectHeader() {
  const { project, loading, role } = useProject();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const basePath = `/projects/${projectId}/`;
  const tabs = (project?.projectType === 'training' ? TRAINING_TABS : TABS).filter(
    (tab) => !tab.requires || canProject(role, tab.requires),
  );
  const activeTab = tabs.find((tab) =>
    tab.end
      ? location.pathname === basePath.slice(0, -1)
      : location.pathname.startsWith(`${basePath}${tab.to}`),
  );

  // On a phone the tab row scrolls sideways; keep the current tab in view (e.g. after following
  // a notification straight to Discussions, which sits past the first few tabs).
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    navRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab?.to]);

  return (
    <header className="border-b border-border bg-surface px-4 sm:px-6">
      <div className="flex h-11 flex-wrap items-center gap-2.5 overflow-hidden sm:h-14">
        <NavLink to="/projects" className="text-sm text-muted hover:text-text">
          Projects
        </NavLink>
        <span className="text-border">/</span>
        <span className="text-sm font-semibold">
          {loading ? 'Loading…' : (project?.name ?? 'Untitled project')}
        </span>
        {project && (
          <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent-soft-fg">
            {project.status === 'archived' ? 'Archived' : 'Active'}
          </span>
        )}
      </div>
      <nav
        ref={navRef}
        className="-mx-4 flex gap-5 overflow-x-auto px-4 [scrollbar-width:none] sm:-mx-6 sm:px-6"
      >
        {tabs.map((tab) => {
          const isActive = activeTab?.to === tab.to;
          return (
            <NavLink
              key={tab.to}
              to={`/projects/${projectId}/${tab.to}`}
              end={tab.end}
              className={`relative flex h-[38px] flex-shrink-0 items-center text-sm ${
                isActive ? 'font-semibold text-text' : 'text-muted hover:text-text'
              }`}
            >
              {tab.label}
              {tab.to === 'discussions' && <UnreadBadge />}
              {isActive && (
                <motion.span
                  layoutId="project-tab-active"
                  className="absolute inset-x-0 bottom-0 h-0.5 bg-accent"
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
            </NavLink>
          );
        })}
      </nav>
    </header>
  );
}

export function ProjectLayout() {
  // Discussions pins its composer to the bottom edge and pads it for the home indicator itself.
  const fillsHeight = useLocation().pathname.endsWith('/discussions');
  return (
    <ProjectProvider>
      <div className="flex h-full flex-col">
        <ProjectHeader />
        <div className="flex-1 overflow-auto">
          <Outlet />
          {!fillsHeight && <div aria-hidden className="h-[env(safe-area-inset-bottom)]" />}
        </div>
      </div>
    </ProjectProvider>
  );
}
